/**
 * initGame — deterministic game setup for Cascadia.
 *
 * Rules:
 * - No I/O, no Math.random() — RNG is injected via `seed`.
 * - No mutations of input objects.
 * - Returns Result<GameState, string> using railway types from types/common.ts.
 *
 * Spec: docs/task-log/20260505-cascadia-online/02-rules-spec.md §6.1.
 */

import { tilesPerPlayerCount } from "./data/bag-supply";
import {
  resolveTileDefId,
  resolveWildlifeSpecies,
} from "./data/instance-resolver";
import { START_CLUSTERS } from "./data/start-tiles";
import { WILDLIFE_SCORING_CARDS_BY_ID } from "./data/wildlife-scoring-cards";
import type { Result } from "./types/common";
import { err, ok } from "./types/common";
import { coordKey } from "./types/coords";
import type { ModuleFlags, PlayerCount, Ruleset } from "./types/game-config";
import type { GameState, ScoringCardSelection } from "./types/game-state";
import type { Market, MarketSlot } from "./types/market";
import type { BoardCell, PlayerState } from "./types/player";
import type { Supply } from "./types/supply";

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface InitGameInput {
  readonly gameId: string;
  readonly seed: string;
  readonly playerSeating: readonly {
    readonly playerId: string;
    readonly displayName: string;
  }[];
  readonly scoringCards: ScoringCardSelection;
  readonly modulesOn: readonly string[];
  readonly hostPlayerId: string;
  readonly createdAt: string;
}

// ---------------------------------------------------------------------------
// Known module names
// ---------------------------------------------------------------------------

const KNOWN_MODULES = new Set([
  "landmarks",
  "family",
  "intermediate",
  "solo",
  "ksPromo",
]);

// ---------------------------------------------------------------------------
// Mulberry32 seeded PRNG
//
// Public domain algorithm. Produces a uint32 on each call.
// We convert the seed string to a uint32 via a simple hash.
// ---------------------------------------------------------------------------

const seedToUint32 = (seed: string): number => {
  let h = 0x12345678;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 0x9e3779b9) >>> 0;
    h = ((h << 13) | (h >>> 19)) >>> 0;
  }
  return h >>> 0;
};

interface Rng {
  next: () => number;
}

const makeMulberry32 = (seed: string): Rng => {
  let state = seedToUint32(seed);

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let z = state;
    z = Math.imul(z ^ (z >>> 15), z | 1) >>> 0;
    z = (z ^ (z + Math.imul(z ^ (z >>> 7), z | 61))) >>> 0;
    z = (z ^ (z >>> 14)) >>> 0;
    return z / 4294967296;
  };

  return { next };
};

// ---------------------------------------------------------------------------
// Fisher-Yates shuffle (mutates in place — only called on freshly created arrays)
// ---------------------------------------------------------------------------

const shuffleInPlace = <T>(arr: T[], rng: Rng): T[] => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
};

// ---------------------------------------------------------------------------
// Instance ID generation
// ---------------------------------------------------------------------------

const makeTileInstanceId = (index: number): string => `tile-inst-${index}`;
const makeTokenInstanceId = (index: number): string => `token-inst-${index}`;

// ---------------------------------------------------------------------------
// Starter cluster board cells (§3.7, §6.1.3)
//
// Starter cluster tiles are "in addition to" the draw pile, so their instance
// ids use a distinct `starter-<seat>-<i>` namespace that can never collide
// with the draw-pile `tile-inst-<n>` ids.
// ---------------------------------------------------------------------------

// Per-tile visual rotation for starter cluster tiles, per the canonical
// wireframe docs/wireframes/cascadia/02-starter-tiles.html (LAYOUT block):
//   key (12 o'clock, single-habitat): rot 0    → engine rotation 0
//   d5  (5 o'clock,  dual-habitat ): rot -120° → engine rotation 4 (= 240° CW)
//   d7  (7 o'clock,  dual-habitat ): rot 0    → engine rotation 0
// Without this, d5 tiles render with the default "\" diagonal seam (rotation
// 0) instead of the vertical seam published in the official starter art.
const STARTER_TILE_ROTATION: Record<string, 0 | 4> = {
  key: 0,
  d5: 4,
  d7: 0,
};

const buildStarterCells = (
  cluster: (typeof START_CLUSTERS)[number],
  seat: number,
): Record<string, BoardCell> => {
  const cells: Record<string, BoardCell> = {};
  cluster.tiles.forEach((t, i) => {
    const suffix = t.tileDefId.split("_").pop() ?? "";
    const rotation = STARTER_TILE_ROTATION[suffix] ?? 0;
    cells[coordKey(t.coord)] = {
      tileInstanceId: `starter-${seat}-${i}`,
      tileDefId: t.tileDefId,
      rotation,
      wildlife: null,
      landmark: null,
    };
  });
  return cells;
};

// ---------------------------------------------------------------------------
// initGame
// ---------------------------------------------------------------------------

export const initGame = (input: InitGameInput): Result<GameState, string> => {
  const {
    gameId,
    seed,
    playerSeating,
    scoringCards,
    modulesOn,
    hostPlayerId,
    createdAt,
  } = input;

  // Validation: modules
  for (const mod of modulesOn) {
    if (!KNOWN_MODULES.has(mod)) {
      return err("ERR_UNKNOWN_MODULE");
    }
  }

  const withLandmarks = modulesOn.includes("landmarks");
  const isSolo = modulesOn.includes("solo");
  const playerCount = playerSeating.length as PlayerCount;

  // Validation: player count
  // Solo allows 1 player (uses 44-tile setup per §8, even without landmarks)
  // Base game supports 2–4 players only.
  // Landmarks supports 1–4 players (5–6 require fastGame, not in scope here).
  const allowedCounts: ReadonlySet<number> = isSolo
    ? new Set([1])
    : withLandmarks
      ? new Set([1, 2, 3, 4])
      : new Set([2, 3, 4]);

  if (!allowedCounts.has(playerCount)) {
    return err("ERR_PLAYER_COUNT");
  }

  // Validate tilesPerPlayerCount is defined for this combination.
  // For solo (1 player without landmarks flag), use the landmarks row per §8.
  const effectiveWithLandmarks = withLandmarks || isSolo;
  const tilesToDraw = tilesPerPlayerCount(playerCount, effectiveWithLandmarks);
  if (tilesToDraw === null) {
    return err("ERR_PLAYER_COUNT");
  }

  // Validation: scoring cards
  const wildlifeKeys: Array<keyof ScoringCardSelection> = [
    "bear",
    "elk",
    "salmon",
    "hawk",
    "fox",
  ];
  for (const wildlife of wildlifeKeys) {
    const cardId = scoringCards[wildlife];
    const card = WILDLIFE_SCORING_CARDS_BY_ID[cardId];
    if (card === undefined) {
      return err("ERR_SCORING_CARD");
    }
    if (card.wildlife !== wildlife) {
      return err("ERR_SCORING_CARD");
    }
  }

  // Validation: host must be seated
  const seatedIds = new Set(playerSeating.map((p) => p.playerId));
  if (!seatedIds.has(hostPlayerId)) {
    return err("ERR_HOST_NOT_SEATED");
  }

  // ---------------------------------------------------------------------------
  // Build supply
  // ---------------------------------------------------------------------------

  const rng = makeMulberry32(seed);

  // Build habitat tile instances — cycle through TILE_DEFS to fill tilesToDraw
  const tileInstanceIds: string[] = [];
  for (let i = 0; i < tilesToDraw; i++) {
    tileInstanceIds.push(makeTileInstanceId(i));
  }
  shuffleInPlace(tileInstanceIds, rng);

  // Build wildlife token instances — cycle through WILDLIFE species
  // Base game has 100 tokens, landmarks adds 35. We generate enough for the
  // draw pile count. The exact count used is from BASE_BAG.wildlifeTokenCount.
  const tokenCount = withLandmarks ? 135 : 100;
  const tokenInstanceIds: string[] = [];
  for (let i = 0; i < tokenCount; i++) {
    tokenInstanceIds.push(makeTokenInstanceId(i));
  }
  shuffleInPlace(tokenInstanceIds, rng);

  // Deal one distinct starter cluster per player (§6.1.3). The shuffle uses
  // the same seeded rng but runs AFTER the tile/token shuffles so the supply
  // deal stays deterministic for any pre-existing seed. There are 5 clusters
  // and at most 4 players, so each seat gets a distinct cluster.
  const clusterPool = shuffleInPlace([...START_CLUSTERS], rng);

  // Deal 4 tiles + 4 tokens to market (from front of shuffled arrays)
  const MARKET_SIZE = 4;
  const marketTileIds = tileInstanceIds.slice(0, MARKET_SIZE);
  const marketTokenIds = tokenInstanceIds.slice(0, MARKET_SIZE);
  const remainingTiles = tileInstanceIds.slice(MARKET_SIZE);
  const remainingTokens = tokenInstanceIds.slice(MARKET_SIZE);

  const supply: Supply = {
    habitatDrawPile: remainingTiles,
    wildlifeBag: remainingTokens,
    natureTokenReserve: withLandmarks ? 40 : 25,
  };

  const buildSlot = (index: 0 | 1 | 2 | 3): MarketSlot => {
    const tileInstanceId = marketTileIds[index]!;
    const tokenInstanceId = marketTokenIds[index]!;
    return {
      index,
      tileInstanceId,
      tokenInstanceId,
      tileDefId: resolveTileDefId(tileInstanceId),
      wildlifeSpecies: resolveWildlifeSpecies(tokenInstanceId),
    };
  };
  const marketSlots: [MarketSlot, MarketSlot, MarketSlot, MarketSlot] = [
    buildSlot(0),
    buildSlot(1),
    buildSlot(2),
    buildSlot(3),
  ];

  const market: Market = {
    slots: marketSlots,
    autoWipePending: false,
    optionalWipeUsedThisTurn: false,
    decoupleUnlockedThisTurn: false,
  };

  // ---------------------------------------------------------------------------
  // Build players
  // ---------------------------------------------------------------------------

  const players: PlayerState[] = playerSeating.map((seat, index) => ({
    playerId: seat.playerId,
    seat: index,
    displayName: seat.displayName,
    natureTokens: 0,
    board: { cells: buildStarterCells(clusterPool[index]!, index) },
    landmarkPicks: [],
    draft: { tileInstanceId: null, tokenInstanceId: null },
    scoreBreakdown: null,
  }));

  // ---------------------------------------------------------------------------
  // Build ruleset
  // ---------------------------------------------------------------------------

  const modules: ModuleFlags = {
    landmarks: withLandmarks,
    ksWildlifePromo: modulesOn.includes("ksPromo"),
    landmarksKsPromo: false,
    solo: isSolo,
    familyVariant: modulesOn.includes("family"),
    intermediateVariant: modulesOn.includes("intermediate"),
    familySkipMajority: false,
    fastGame: false,
  };

  const ruleset: Ruleset = {
    mode: withLandmarks ? "landmarks" : "base",
    modules,
    playerCount,
    tileOrientation: "pointy-top",
  };

  // ---------------------------------------------------------------------------
  // Assemble GameState
  // ---------------------------------------------------------------------------

  const state: GameState = {
    gameId,
    version: 0,
    createdAt,
    ruleset,
    players,
    market,
    supply,
    scoringCards,
    turn: {
      activeSeat: 0,
      phase: "marketCheck",
      actionBudget: {
        natureSpendable: 0,
        landmarkEligibleHabitats: [],
        justPlacedTileCoord: null,
      },
    },
    rng: {
      algorithm: "mulberry32",
      seed,
    },
    winners: null,
  };

  return ok(state);
};
